import { requireSupabase, supabaseEnabled } from '@/lib/supabase';
import { saveLocalDesign, type SerializedDesign } from '@/lib/designState';

export type SaveDesignResult = {
  id: string;
  shareSlug: string | null;
  mode: 'supabase' | 'local';
};

function randomSlug(): string {
  return (
    Math.random().toString(36).slice(2, 8) +
    Math.random().toString(36).slice(2, 6)
  );
}

function stripForDb(design: SerializedDesign) {
  return {
    product_id: design.productId,
    // template_id is uuid FK — our template ids are slugs; keep null and store in state
    template_id: null,
    name: `Design ${design.productId}`,
    state: design as unknown as Record<string, unknown>,
  };
}

/**
 * Persist design state.
 * - Supabase when configured (RLS: anon insert with user_id null allowed after patch)
 * - localStorage always as fallback / offline
 */
export async function saveDesignToCloud(
  design: SerializedDesign,
  opts: { isPublic?: boolean } = {},
): Promise<SaveDesignResult> {
  const localOnly = await persistLocal(design);

  if (!supabaseEnabled) {
    return { id: localOnly, shareSlug: null, mode: 'local' };
  }

  try {
    const db = await requireSupabase();
    const shareSlug = opts.isPublic ? randomSlug() : null;
    const row = {
      ...stripForDb(design),
      user_id: null,
      is_public: Boolean(opts.isPublic),
      share_slug: shareSlug,
    };

    // Private rows are not SELECT-able until patch-after-schema.sql is applied;
    // Prefer: return=minimal avoids RETURNING (RLS 42501 on representation).
    if (!opts.isPublic) {
      const { error } = await db.from('designs').insert(row);
      if (error) throw error;
      return { id: localOnly, shareSlug: null, mode: 'supabase' };
    }

    const { data, error } = await db
      .from('designs')
      .insert(row)
      .select('id, share_slug')
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error('Design insert returned no row (RLS?)');
    return {
      id: String(data.id ?? localOnly),
      shareSlug: data.share_slug ?? shareSlug,
      mode: 'supabase',
    };
  } catch (e) {
    console.warn('[saveDesign] supabase failed, local only', e);
    return { id: localOnly, shareSlug: null, mode: 'local' };
  }
}

export async function loadDesignBySlug(slug: string): Promise<SerializedDesign | null> {
  if (!supabaseEnabled) return null;
  try {
    const db = await requireSupabase();
    const { data, error } = await db
      .from('designs')
      .select('state')
      .eq('share_slug', slug)
      .eq('is_public', true)
      .maybeSingle();
    if (error) throw error;
    if (!data?.state) return null;
    return data.state as unknown as SerializedDesign;
  } catch (e) {
    console.warn('[loadDesignBySlug]', e);
    return null;
  }
}

async function persistLocal(design: SerializedDesign): Promise<string> {
  const id = `local-${Date.now().toString(36)}`;
  saveLocalDesign(design);
  return id;
}
